import { DatePipe } from "@angular/common";
import { Component, makeStateKey, OnDestroy, OnInit, signal, TransferState, ViewChild } from "@angular/core";
import { MatPaginator, MatPaginatorModule } from "@angular/material/paginator";
import { MatTableModule } from "@angular/material/table";
import { MetaDefinition } from "@angular/platform-browser";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { firstValueFrom, Subscription } from "rxjs";
import { ConfigService } from "src/app/servicios/config/config.service";
import { PlatformService } from "src/app/servicios/platform/platform.service";
import { KebabCasePipe } from "../../pipes/kebab-case.pipe";
import { NoticiasService } from "../../servicios/noticias/noticias.service";
import { CanonicalService } from "@src/app/servicios/canonical/canonical.service";

const TRANSFER_KEY = makeStateKey<any>("hemeroteca");

@Component({
  selector: "app-hemeroteca",
  templateUrl: "./hemeroteca.component.html",
  styleUrls: ["./hemeroteca.component.css"],
  imports: [MatTableModule, RouterLink, MatPaginatorModule, DatePipe, KebabCasePipe],
})
export class HemerotecaComponent implements OnInit, OnDestroy {
  columnsToDisplay = ["tituloVideo", "idVideo", "urlVideo"];
  videos: any = signal([]);
  constantesGlobales;

  currentPage = 0;
  pageLength = 10;
  totalLength = 0;
  rSub: Subscription;
  @ViewChild(MatPaginator, { static: true }) paginator?: MatPaginator;

  constructor(
    private readonly noticiasService: NoticiasService,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly platformService: PlatformService,
    private readonly configService: ConfigService,
    private readonly transferState: TransferState,
    private readonly canonicalService: CanonicalService,
  ) {}

  /**
   * Método de ciclo de vida de Angular que se ejecuta al inicializar el componente.
   *
   * - Obtiene las constantes globales desde el servicio de configuración.
   * - Traduce el paginador para adaptarlo al idioma correspondiente.
   * - Se suscribe a los parámetros de la ruta activa para obtener el identificador de la página actual
   *   y luego carga los datos de la hemeroteca.
   *
   * @returns {void}
   */
  ngOnInit(): void {
    this.constantesGlobales = this.configService.getConstantesGlobales();
    this.traducirPaginator();
    this.rSub = this.activatedRoute.params.subscribe((parms: any) => {
      if (parms.id) {
        this.currentPage = parms.id;
      }
      this.getHemeroteca();
    });
  }

  /**
   * Obtiene la hemeroteca dependiendo del entorno de ejecución.
   *
   * - Si no se está ejecutando en un navegador, se llama al método `fetchNewsAndStoreKey`
   *   para obtener las noticias y almacenarlas.
   * - Si se está ejecutando en un navegador y existe una clave en el estado de transferencia (`TRANSFER_KEY`):
   *   - Recupera las noticias almacenadas en el estado de transferencia.
   *   - Elimina la clave del estado de transferencia.
   *   - Establece las noticias recuperadas mediante el método `setNews`.
   * - Si no existe la clave en el estado de transferencia, se llama a `fetchNewsAndStoreKey`.
   *
   * @remarks
   * Este método utiliza `platformService` para determinar si el código se ejecuta en un navegador
   * y `transferState` para manejar el estado compartido entre el servidor y el cliente.
   */
  getHemeroteca() {
    if (!this.platformService.isBrowser()) {
      this.fetchNewsAndStoreKey();
    } else {
      if (this.transferState.hasKey(TRANSFER_KEY)) {
        const news = this.transferState.get<any>(TRANSFER_KEY, null);
        this.transferState.remove(TRANSFER_KEY);
        this.setNews(news);
      } else {
        this.fetchNewsAndStoreKey();
      }
    }
  }

  /**
   * Obtiene las noticias desde el servicio `noticiasService` y las almacena en el estado de transferencia.
   *
   * Este método realiza una solicitud para obtener todas las noticias basándose en la página actual,
   * la longitud de la página y el ámbito definido en las constantes globales. Una vez que se obtienen
   * los resultados, se almacenan en el estado local del componente y en el estado de transferencia
   * para su uso posterior.
   *
   * @returns {void} Este método no devuelve ningún valor.
   */
  fetchNewsAndStoreKey(): void {
    const allNews = this.noticiasService.getAllNews(this.currentPage, this.pageLength, this.constantesGlobales.idAmbito);
    if (allNews) {
      firstValueFrom(allNews).then((result) => {
        this.setNews(result);
        this.transferState.set(TRANSFER_KEY, result);
      });
    }
  }

  /**
   * Establece las noticias en el componente de hemeroteca.
   *
   * @param hemeroteca - Objeto que contiene los datos de la hemeroteca, incluyendo
   *                     el listado de videos y el total de videos.
   *
   * Este método actualiza el listado de videos y la longitud total de videos
   * en base a los datos proporcionados en el objeto `hemeroteca`. Además,
   * actualiza las metaetiquetas relacionadas con el contenido.
   */
  setNews(hemeroteca): void {
    this.videos.set(hemeroteca["listadoVideos"]);
    this.totalLength = hemeroteca["totalVideos"];
    this.updateMetaTags();
  }

  /**
   * Método del ciclo de vida de Angular que se ejecuta cuando el componente se destruye.
   * Aquí se asegura de liberar recursos, como desuscribirse de observables para evitar
   * fugas de memoria.
   */
  ngOnDestroy(): void {
    this.rSub.unsubscribe();
  }

  /**
   * Traduce las etiquetas del paginador a español.
   *
   * Este método personaliza los textos mostrados en el componente de paginación,
   * incluyendo las etiquetas para los elementos por página, la navegación entre
   * páginas (siguiente, anterior) y las etiquetas para la primera y última página.
   *
   * @returns {void} No retorna ningún valor.
   */
  traducirPaginator(): void {
    this.paginator._intl.itemsPerPageLabel = "Noticias por página:";
    this.paginator._intl.nextPageLabel = "Página siguiente";
    this.paginator._intl.previousPageLabel = "Página anterior";
    this.paginator._intl.lastPageLabel = "Última página";
    this.paginator._intl.firstPageLabel = "Primera página";
  }

  /**
   * Maneja el evento de cambio de página en un componente de paginación.
   * Actualiza las propiedades `pageLength` y `currentPage` basadas en el evento recibido,
   * y navega a la URL correspondiente según la página actual.
   *
   * @param event - Objeto del evento que contiene información sobre el cambio de página,
   *                incluyendo `pageSize` (tamaño de la página) y `pageIndex` (índice de la página actual).
   */
  pageChanged(event): void {
    this.pageLength = event.pageSize;
    this.currentPage = event.pageIndex;
    let url = "/hemeroteca";
    if (this.currentPage !== 0) {
      url = "/hemeroteca/" + this.currentPage;
    }
    this.router.navigateByUrl(url);
  }

  /**
   * Actualiza las metaetiquetas de la página con información relevante
   * para SEO y redes sociales. Genera un arreglo de metaetiquetas basado
   * en las constantes globales y la fecha actual, y luego actualiza las
   * URLs canónicas utilizando el servicio `canonicalService`.
   *
   * @private
   */
  private updateMetaTags(): void {
    const metaTagsArray: MetaDefinition[] = [
      { property: "og:title", content: this.constantesGlobales.siteTitle },
      { property: "og:type", content: "website" },
      { property: "og:image", content: `${this.constantesGlobales.siteDomain}/assets/images/${this.constantesGlobales.imgShare}` },
      { property: "og:url", content: this.constantesGlobales.siteDomain },
      { property: "og:description", content: this.constantesGlobales.metaTag },
      { property: "article:author", content: this.constantesGlobales.siteName },
      { property: "article:publisher", content: this.constantesGlobales.siteName },
      { property: "og:site_name", content: this.constantesGlobales.siteDomain },
      { name: "DC.title", content: this.constantesGlobales.siteTitle },
      { name: "DC.creator", content: this.constantesGlobales.siteTitle },
      { name: "DC.publisher", content: this.constantesGlobales.siteTitle },
      { name: "DC.description", content: this.constantesGlobales.metaTag },
      { name: "DC.subject", content: this.constantesGlobales.metaTag },
      { name: "DC.date", content: new Date().toISOString() },
    ];
    this.canonicalService.updateCanonicalURls(metaTagsArray);
  }
}
